import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Mail, ExternalLink } from "lucide-react";

/**
 * Privacy Policy page — /privacy
 *
 * Google Play-compliant privacy policy for Distill GS. Rendered inside the
 * same dark-mode canvas as the rest of the app, using DISTILL's brand tokens
 * (#111622 canvas, #1A2232 surface, #F8FAFC body, #94A3B8 muted, #F2BD68 amber
 * accent, #E85A2A coral CTA) so it feels native — not like a legal boilerplate
 * paste.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#111622]">
      <div
        className="mx-auto w-full max-w-3xl px-4 sm:px-6 pt-6"
        style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
      >
        {/* Back-to-app affordance so the page never feels like a dead end. */}
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#94A3B8] hover:text-[#F8FAFC] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Back to DISTILL
        </Link>

        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#F2BD68]/40 bg-[#F2BD68]/10 px-3 py-1 text-[10.5px] font-semibold tracking-[0.16em] uppercase text-[#F2BD68]">
          <ShieldCheck className="h-3 w-3" aria-hidden />
          Privacy Policy
        </div>

        <h1
          className="mt-4 font-semibold tracking-tight text-[#F8FAFC] break-words"
          style={{ fontSize: "clamp(24px, 6vw, 32px)", lineHeight: 1.2 }}
        >
          Privacy Policy for Distill GS
        </h1>
        <p className="mt-2 text-[13px] text-[#94A3B8]" data-testid="privacy-last-updated">
          Last Updated: September 2026
        </p>

        <p
          className="mt-6 text-[#E2E8F0]"
          style={{ fontSize: "clamp(14.5px, 3.9vw, 16px)", lineHeight: 1.75 }}
        >
          Distill GS (&ldquo;we&rdquo;, &ldquo;our&rdquo;, or &ldquo;us&rdquo;) operates the Distill GS
          mobile application and website at{" "}
          <ExternalLinkInline href="https://distillgs.in">https://distillgs.in</ExternalLinkInline>{" "}
          (collectively, the &ldquo;Service&rdquo;). We are committed to protecting your privacy and
          handling your data with complete transparency.
        </p>
        <p
          className="mt-4 text-[#E2E8F0]"
          style={{ fontSize: "clamp(14.5px, 3.9vw, 16px)", lineHeight: 1.75 }}
        >
          This Privacy Policy explains what information we collect, how it is used, how it is stored,
          and your rights regarding your personal data when using Distill GS.
        </p>

        <Section title="1. Information We Collect">
          <p>
            We believe in radical data minimization. We only collect the bare minimum information
            necessary to provide you with a fast, distraction-free study experience:
          </p>

          <SubSection title="A. Guest Usage (Unauthenticated Users)">
            <ul className="space-y-2 list-disc pl-5 marker:text-[#F2BD68]/60">
              <li>
                <span className="font-semibold text-[#F8FAFC]">What is stored:</span> Your card
                reading history, completion state, daily streak counters, and saved/bookmarked
                cards.
              </li>
              <li>
                <span className="font-semibold text-[#F8FAFC]">Where it is stored:</span>{" "}
                Exclusively on your local device via standard browser localStorage.
              </li>
              <li>
                <span className="font-semibold text-[#F8FAFC]">Server transmission:</span> None of
                this reading activity is linked to your identity or stored on our servers under an
                individual personal profile.
              </li>
            </ul>
          </SubSection>

          <SubSection title="B. Account Information (Optional Google Sign-In)">
            <p>If you choose to authenticate using Google Sign-In, we collect:</p>
            <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-[#F2BD68]/60">
              <li>Full Name</li>
              <li>Email Address</li>
              <li>Profile Avatar URL (optional, as provided by Google)</li>
            </ul>
            <p className="mt-3">
              <span className="font-semibold text-[#F8FAFC]">Purpose:</span> Strictly to authenticate
              your account and synchronize your bookmarked cards, revision history, and reading
              progress across your devices.
            </p>
          </SubSection>

          <SubSection title="C. Technical & Diagnostic Data">
            <ul className="space-y-2 list-disc pl-5 marker:text-[#F2BD68]/60">
              <li>
                Like all web services, our servers automatically log basic technical requests for
                security, crash prevention, and service reliability. This may include your IP
                address, device type, operating system version, app version, and access timestamps.
              </li>
              <li>
                This diagnostic data is aggregated, anonymized, and never merged with your personal
                profile.
              </li>
            </ul>
          </SubSection>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the minimal data collected strictly for legitimate app operations:</p>
          <ul className="mt-2 space-y-2 list-disc pl-5 marker:text-[#F2BD68]/60">
            <li>To deliver, maintain, and update daily revision cards and GS feeds.</li>
            <li>To authenticate registered users and securely sync study progress across devices.</li>
            <li>To detect, prevent, and troubleshoot technical bugs, crashes, and server abuse.</li>
            <li>To respond to direct user support inquiries or feedback submitted to our team.</li>
          </ul>

          <p className="mt-5 font-semibold text-[#F8FAFC]">We do NOT:</p>
          <ul className="mt-2 space-y-2 list-disc pl-5 marker:text-[#E85A2A]/70">
            <li>Sell, rent, lease, or monetize your personal data in any way.</li>
            <li>Use third-party behavioral ad trackers or tracking SDKs.</li>
            <li>
              Serve third-party programmatic display advertisements (AdMob, Meta Audience Network,
              etc.).
            </li>
            <li>Share your personal information with data brokers or marketing agencies.</li>
          </ul>
        </Section>

        <Section title="3. Third-Party Services & Integrations">
          <p>We partner only with essential infrastructure providers to deliver the Service:</p>
          <ul className="mt-2 space-y-3 list-disc pl-5 marker:text-[#F2BD68]/60">
            <li>
              <span className="font-semibold text-[#F8FAFC]">Google Identity Services (OAuth):</span>{" "}
              Handles secure authentication when you choose to sign in with Google. You can review
              Google&rsquo;s privacy practices at{" "}
              <ExternalLinkInline href="https://policies.google.com/privacy">
                https://policies.google.com/privacy
              </ExternalLinkInline>
              .
            </li>
            <li>
              <span className="font-semibold text-[#F8FAFC]">Cloud & Hosting Infrastructure:</span>{" "}
              Our application backend and APIs are hosted on secure cloud infrastructure providers
              that process requests in compliance with industry-standard data protection protocols.
            </li>
            <li>
              <span className="font-semibold text-[#F8FAFC]">External Reference Links:</span> Some
              revision cards contain citations or direct links to primary public sources (such as
              PIB, PRS Legislative Research, or national news outlets). Tapping these links opens
              third-party websites that operate under their own independent privacy policies.
            </li>
          </ul>
        </Section>

        <Section title="4. Data Security & Encryption">
          <ul className="space-y-2 list-disc pl-5 marker:text-[#F2BD68]/60">
            <li>
              <span className="font-semibold text-[#F8FAFC]">Data in Transit:</span> All
              communications between the Distill GS client and our backend servers are encrypted
              using modern Transport Layer Security (TLS/HTTPS).
            </li>
            <li>
              <span className="font-semibold text-[#F8FAFC]">Data Storage:</span> User credentials
              are never stored directly on our servers; authentication tokens are securely managed
              via Google&rsquo;s OAuth protocol.
            </li>
          </ul>
        </Section>

        <Section title="5. Data Retention and Account & Data Deletion">
          <p>
            In full compliance with Google Play&rsquo;s User Data and Account Deletion policies, you
            have complete control over your data:
          </p>

          <SubSection title="A. Deleting Guest Data">
            <p>
              Because guest activity is stored client-side, you can permanently erase all reading
              history, streaks, and bookmarks at any moment by clearing your browser cache and local
              storage, or by uninstalling the application.
            </p>
          </SubSection>

          <SubSection title="B. Deleting Registered Accounts and Synced Data">
            <ul className="space-y-2 list-disc pl-5 marker:text-[#F2BD68]/60">
              <li>
                If you have created an account using Google Sign-In, you may request permanent
                deletion of your account and all associated cloud data at any time.
              </li>
              <li>
                <span className="font-semibold text-[#F8FAFC]">How to request deletion:</span> Send
                an email to{" "}
                <a
                  href="mailto:distillgs@gmail.com?subject=Request%20Data%20Deletion%20-%20Distill%20GS"
                  data-testid="privacy-deletion-email"
                  className="font-semibold text-[#F2BD68] underline decoration-[#F2BD68]/40 underline-offset-2 hover:text-[#E85A2A] hover:decoration-[#E85A2A]/60 transition-colors"
                >
                  distillgs@gmail.com
                </a>{" "}
                with the subject line: &ldquo;Request Data Deletion - Distill GS&rdquo;.
              </li>
              <li>
                <span className="font-semibold text-[#F8FAFC]">What gets deleted:</span> Within 30
                days of receiving your verified request, we will permanently purge your name, email
                address, profile record, and all cloud-synced revision data from our production
                databases.
              </li>
            </ul>
          </SubSection>
        </Section>

        <Section title="6. Children's Privacy (COPPA & Policy Compliance)">
          <p>
            Distill GS is designed for adult students and aspirants preparing for competitive civil
            services examinations. The Service is not directed toward or intended for children under
            the age of 13 (or under 16 in certain jurisdictions).
          </p>
          <p className="mt-4">
            We do not knowingly collect or solicit personal identifiable information from children.
            If we discover that a child under 13 has provided us with personal information, we will
            immediately delete that information from our records. If you are a parent or guardian
            and believe your child has registered an account, please contact us at{" "}
            <a
              href="mailto:distillgs@gmail.com"
              className="font-semibold text-[#F2BD68] underline decoration-[#F2BD68]/40 underline-offset-2 hover:text-[#E85A2A] hover:decoration-[#E85A2A]/60 transition-colors"
            >
              distillgs@gmail.com
            </a>
            .
          </p>
        </Section>

        <Section title="7. Changes to This Privacy Policy">
          <p>
            We may periodically update this Privacy Policy to reflect app enhancements, operational
            changes, or legal updates. Any revisions will be published directly on this page (
            <ExternalLinkInline href="https://distillgs.in/privacy">
              https://distillgs.in/privacy
            </ExternalLinkInline>
            ) with an updated &ldquo;Last Updated&rdquo; date. Continued use of the Service following
            any update constitutes acceptance of the revised terms.
          </p>
        </Section>

        <Section title="8. Contact Information">
          <p>
            If you have any questions, feedback, or data privacy requests regarding this Privacy
            Policy or Distill GS, please contact us directly:
          </p>
          <ul className="mt-3 space-y-2 list-disc pl-5 marker:text-[#F2BD68]/60">
            <li>
              <span className="font-semibold text-[#F8FAFC]">App Name:</span> Distill GS
            </li>
            <li>
              <span className="font-semibold text-[#F8FAFC]">Official Website:</span>{" "}
              <ExternalLinkInline href="https://distillgs.in">https://distillgs.in</ExternalLinkInline>
            </li>
            <li>
              <span className="font-semibold text-[#F8FAFC]">Direct Email:</span>{" "}
              <a
                href="mailto:distillgs@gmail.com"
                className="font-semibold text-[#F2BD68] underline decoration-[#F2BD68]/40 underline-offset-2 hover:text-[#E85A2A] hover:decoration-[#E85A2A]/60 transition-colors"
              >
                distillgs@gmail.com
              </a>
            </li>
          </ul>

          <a
            href="mailto:distillgs@gmail.com"
            data-testid="privacy-contact-email"
            className="mt-5 inline-flex items-center gap-2 rounded-xl border border-[#26334D] bg-[#1A2232] px-3.5 py-2 text-sm font-semibold text-[#F2BD68] hover:bg-[#232E45] hover:text-[#E85A2A] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2BD68]/60"
          >
            <Mail className="h-4 w-4" aria-hidden />
            Contact distillgs@gmail.com
          </a>
        </Section>

        <div className="mt-10 pt-6 border-t border-[#26334D]">
          <p className="text-[11px] text-[#64748B]" style={{ lineHeight: 1.6 }}>
            This policy will be updated as the app evolves. Material changes will be surfaced in the
            app before they take effect.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Sub-section wrapper — consistent title + body typography so every part of
 * the policy uses the same visual rhythm.
 */
function Section({ title, children }) {
  return (
    <section className="mt-10 pt-8 border-t border-[#26334D]">
      <h2
        className="font-semibold tracking-tight text-[#F8FAFC] break-words"
        style={{ fontSize: "clamp(18px, 4.6vw, 20px)", lineHeight: 1.35 }}
      >
        {title}
      </h2>
      <div
        className="mt-3 text-[#CBD5E1] space-y-1"
        style={{ fontSize: "clamp(14px, 3.8vw, 15.5px)", lineHeight: 1.75 }}
      >
        {children}
      </div>
    </section>
  );
}

/**
 * Nested heading for A / B / C style subsections inside a numbered section.
 */
function SubSection({ title, children }) {
  return (
    <div className="mt-5">
      <h3
        className="font-semibold text-[#F8FAFC] break-words"
        style={{ fontSize: "clamp(14.5px, 3.9vw, 16px)", lineHeight: 1.4 }}
      >
        {title}
      </h3>
      <div className="mt-2 text-[#CBD5E1]">{children}</div>
    </div>
  );
}

/**
 * External link with subtle amber underline that becomes coral on hover.
 * Always opens in a new tab with safe rel attributes.
 */
function ExternalLinkInline({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-baseline gap-0.5 font-medium text-[#F2BD68] underline decoration-[#F2BD68]/40 underline-offset-2 hover:text-[#E85A2A] hover:decoration-[#E85A2A]/60 transition-colors"
    >
      {children}
      <ExternalLink className="h-3 w-3 translate-y-[1px]" aria-hidden />
    </a>
  );
}
